import React, { useEffect, useState, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";

import Grid from "@mui/material/Grid";
import { useTheme } from "@mui/material/styles";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import { listMyTeams, createTeam } from "services/teams";

// Bouton d’ouverture d’une team (typé pour ESLint)
const OpenCell = ({ row, onOpen }) => (
  <Button size="small" variant="outlined" onClick={() => onOpen(row.original.id)}>
    Open
  </Button>
);
OpenCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    }).isRequired,
  }).isRequired,
  onOpen: PropTypes.func.isRequired,
};

export default function TeamsList() {
  const theme = useTheme();
  const navigate = useNavigate();

  // ── State
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);

  const [open, setOpen] = useState(false);
  const [teamName, setTeamName] = useState("");
  const [teamDesc, setTeamDesc] = useState("");
  const [creating, setCreating] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // ── Data fetch
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const arr = await listMyTeams(); // service renvoie déjà un array
      setTeams(Array.isArray(arr) ? arr : []);
    } catch (e) {
      setError("Failed to load teams.");
      setTeams([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // ── Actions
  const onOpenTeam = useCallback(
    (teamId) => {
      navigate(`/teams/${teamId}`);
    },
    [navigate]
  );

  const onCreate = async () => {
    if (!teamName.trim()) return;
    setCreating(true);
    setError("");
    setSuccess("");
    try {
      const created = await createTeam({
        name: teamName.trim(),
        description: teamDesc.trim(),
      });
      setOpen(false);
      setTeamName("");
      setTeamDesc("");
      setSuccess("Team created.");
      navigate(`/teams/${created.id}`);
    } catch (e) {
      setError("Failed to create team.");
    } finally {
      setCreating(false);
    }
  };

  // ── Table
  const columns = useMemo(
    () => [
      { Header: "Team", accessor: "name", align: "left" },
      { Header: "Description", accessor: "description", align: "left" },
      {
        Header: "Actions",
        accessor: "actions",
        align: "center",
        // ne pas déstructurer { row } -> évite react/prop-types
        Cell: (cell) => <OpenCell row={cell.row} onOpen={onOpenTeam} />,
      },
    ],
    [onOpenTeam]
  );

  const rows = useMemo(
    () =>
      (teams || []).map((t) => ({
        id: t.id,
        name: t.name,
        description: t.description || "-",
        actions: null, // la colonne rend son propre bouton via Cell
      })),
    [teams]
  );

  // ── Render (même squelette que Dashboard)
  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox
        py={3}
        px={2}
        sx={{
          borderRadius: "16px",
          transition: "all 0.3s ease",
          boxShadow:
            theme.palette.mode === "dark"
              ? "0 0 12px rgba(0,0,0,0.4)"
              : "0px 4px 24px rgba(0,0,0,0.05)",
        }}
      >
        {/* Titre + bouton à droite */}
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={6}>
            <MDTypography variant="h5" fontWeight="medium">
              Teams
            </MDTypography>
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
            display="flex"
            justifyContent={{ xs: "flex-start", md: "flex-end" }}
          >
            <Button variant="contained" color="info" onClick={() => setOpen(true)}>
              Create team
            </Button>
          </Grid>
        </Grid>

        {/* Table */}
        <MDBox mt={3}>
          {loading ? (
            <MDBox display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </MDBox>
          ) : (
            <>
              {error && <p style={{ color: "red", marginLeft: 4 }}>{error}</p>}
              {success && <p style={{ color: "green", marginLeft: 4 }}>{success}</p>}

              {rows.length === 0 ? (
                <MDBox px={1} py={2}>
                  <MDTypography variant="button" color="text">
                    No teams yet.
                  </MDTypography>
                </MDBox>
              ) : (
                <DataTable
                  table={{ columns, rows }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                />
              )}
            </>
          )}
        </MDBox>
      </MDBox>

      <Footer />

      {/* Dialog Create Team */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create a Team</DialogTitle>
        <DialogContent>
          <TextField
            label="Team name"
            value={teamName}
            onChange={(e) => setTeamName(e.target.value)}
            fullWidth
            margin="normal"
            autoFocus
          />
          <TextField
            label="Description (optional)"
            value={teamDesc}
            onChange={(e) => setTeamDesc(e.target.value)}
            fullWidth
            margin="normal"
            multiline
            minRows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={creating}>
            Cancel
          </Button>
          <Button
            onClick={onCreate}
            color="primary"
            variant="contained"
            disabled={creating || !teamName.trim()}
          >
            {creating ? "Creating..." : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}
